import { SortDirectionEnum } from '@legacy/shared-types/enums/sort-direction.enum';
import { useQueryClient } from '@tanstack/react-query';
import { Table } from '@components/common/Table/Table';
import { TableBody } from '@components/common/Table/TableBody/TableBody';
import { TableCell } from '@components/common/Table/TableCell/TableCell';
import { TableContainer } from '@components/common/Table/TableContainer/TableContainer';
import { TableHead } from '@components/common/Table/TableHead/TableHead';
import { TableRow } from '@components/common/Table/TableRow/TableRow';
import { TableSortLabel } from '@components/common/Table/TableSortLabel/TableSortLabel';
import { EditIcon } from '@components/common/Icons/EditIcon';
import { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { getPageURLWithSection, PageURL } from '@router/pageURL';
import { btlApplicationFormApi } from '@api/btlApplicationForm/btlApplicationFormApi';
import { useBtlApplications } from '@queries/btlApplications/useBtlApplications';
import { BtlApplicationFormPageEnum } from '../../BtlApplicationFormPage/types';
import { FormattedTableCell } from '../ApplicationsTable/FormattedTableCell';
import { BtlApplicationForListingDto, IApplicationsAnalystTableProps, tableColumnsNames } from './types';
import { allCapsToTitleCase, prettifyEnum } from '@common/helpers';

export const BtlApplicationsTable: FC<IApplicationsAnalystTableProps> = ({ searchInputValue }) => {
  const [order, setOrder] = useState<SortDirectionEnum>(SortDirectionEnum.DESC);
  const [orderBy, setOrderBy] = useState<keyof BtlApplicationForListingDto>('createdAt');
  const { data: allBtlApplications, isLoading } = useBtlApplications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const formattedApplications = allBtlApplications?.map((application: BtlApplicationForListingDto) => ({
    id: application.id,
    case: application.title,
    purpose: allCapsToTitleCase(application.purpose),
    financingAmount: application.financingAmount,
    referenceNumber: application.referenceNumber,
    applicant: application.applicant,
    coApplicant: application.coApplicant,
    status: prettifyEnum(application.status),
    createdAt: application.createdAt,
  }));

  const sortedRows = formattedApplications?.sort((a: any, b: any) => {
    if (a[orderBy] < b[orderBy]) {
      return order === SortDirectionEnum.ASC ? -1 : 1;
    }

    if (a[orderBy] > b[orderBy]) {
      return order === SortDirectionEnum.ASC ? 1 : -1;
    }
    return 0;
  });

  const filteredBySearchInputValue = sortedRows?.filter((row: BtlApplicationForListingDto) => {
    const rowValues = Object.values(row);
    return rowValues.some((value: any) => value?.toString()?.toLowerCase().includes(searchInputValue?.toLowerCase()));
  });

  const sortHandler = (column: keyof BtlApplicationForListingDto) => {
    const isAsc = order === SortDirectionEnum.ASC;
    const isSameColumn = orderBy === column;

    setOrder(isAsc && isSameColumn ? SortDirectionEnum.DESC : SortDirectionEnum.ASC);
    setOrderBy(column);
  };

  const applicationDetailsPreloadHandler = async (applicationId: string | number) => {
    await queryClient.prefetchQuery(['btl-application-in-progress', applicationId], () =>
      btlApplicationFormApi.getApplicationInProgress(String(applicationId)),
    );

    const editBtlApplicationUrl = getPageURLWithSection(
      PageURL.BTL_APPLICATION_EDIT,
      { id: applicationId },
      { section: BtlApplicationFormPageEnum.CASE_SUMMARY },
    );
    navigate(editBtlApplicationUrl);
  };

  return (
    <div className={'mb-2 w-full overflow-hidden rounded-sm bg-white shadow-sm'}>
      <TableContainer>
        <Table aria-label={'collapsible table'}>
          <TableHead>
            <TableRow>
              <TableCell />
              {tableColumnsNames.map(columnName => (
                <TableCell key={columnName}>
                  <TableSortLabel
                    data-value={columnName}
                    active={orderBy === columnName}
                    direction={order}
                    onClick={() => sortHandler(columnName as keyof BtlApplicationForListingDto)}
                  >
                    <FormattedMessage id={`application.${columnName}`} />
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody isLoading={isLoading} columnsNumber={tableColumnsNames.length}>
            {filteredBySearchInputValue?.length ? (
              filteredBySearchInputValue.map((row: BtlApplicationForListingDto, index: number) => (
                <TableRow key={index}>
                  <TableCell />
                  {Object.entries(row)
                    .filter(value => value[0] !== 'id' && value[0] !== 'coApplicant')
                    .map(([objectKey, value]: [string, any]) => (
                      <TableCell key={objectKey}>
                        <FormattedTableCell objectKey={objectKey} value={value} />
                      </TableCell>
                    ))}
                  <TableCell>
                    <button
                      className={'inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0'}
                      onClick={() => applicationDetailsPreloadHandler(row.id)}
                    >
                      <EditIcon htmlColor={'grey'} />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className={'text-center'} colSpan={tableColumnsNames.length}>
                  <FormattedMessage id={'common.itemsNotFound'} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
